import * as names from "@/store/names";
import { activityEvaluationType } from "@/utils/instructional_activity_types";
import useAiBuilderStream from "@/components/reusable/ai-builder/useAiBuilderStream";

/**
 * E38 · Guardado de una evaluación desde el Diseño Instruccional.
 *
 * Desde E37 la pieza del diseño ES la evaluación del Programa, así que guardarla es el mismo
 * flujo de siempre: el wrapper, su `evaluations2.Evaluation` con el scope y el tipo, el enlace
 * entre los dos, los instrumentos y las horas repartidas por Ambiente de Aprendizaje.
 *
 * Está acá y no duplicado en cada punto del DI porque los dos niveles —Asignatura base y
 * Asignatura del Perfil de Egreso— sólo difieren en el endpoint, el nombre de la FK y el
 * número de scope. Una implementación parametrizada cubre los dos, y la comparten tanto crear
 * como editar.
 *
 * No se tocó el Programa: allá este flujo vive repetido en sus cuatro componentes, y
 * consolidarlo habría significado refactorizar el guardado de evaluaciones que usan todas las
 * instituciones. Es una deuda conocida y deliberada — ver el plan E37/E38.
 */

const LEVELS = {
  matter: {
    wrapper_url: "/teaching/matter-evaluation/",
    time_url: "/mesh/mat-eva-stu-env-tim-all/",
    wrapper_fk: "matter_evaluation",
    scope: 3,
    patch_action: names.PATCH_MATTER_EVALUATION,
    patch_id_key: "matter_evaluation_id",
  },
  egress_profile_matter: {
    wrapper_url: "/teaching/egress-profile-matter-evaluation/",
    time_url: "/mesh/egr-pro-mat-eva-stu-env-tim-all/",
    wrapper_fk: "egress_profile_matter_evaluation",
    scope: 2,
    patch_action: names.PATCH_EGRESS_PROFILE_MATTER_EVALUATION,
    patch_id_key: "egress_profile_matter_evaluation_id",
  },
};

function levelFor(level_key) {
  return LEVELS[level_key] || LEVELS.egress_profile_matter;
}

// El tipo del wrapper y el de evaluations2 NO usan los mismos números, y nunca los usaron.
// wrapper: 1 Diagnóstica · 2 Formativa · 3 Sumativa · 4 Actividad · 5 Transversal
// evaluations2: 1 Sumativa · 2 Diagnóstica · 3 Formativa · 4 Transversal
function evaluationTypeFor(wrapper) {
  if (wrapper.type == 1) return 2;
  if (wrapper.type == 2) return 3;
  if (wrapper.type == 3) return 1;
  if (wrapper.type == 5) return 4;
  // El tipo 4 no tiene equivalente: "Actividad" es el tipo visible y Formativa/Sumativa es su
  // subtipo, que se deduce de si pondera. Mismo criterio que usa el Programa.
  if (wrapper.type == 4) return activityEvaluationType(wrapper.weighing);
  return 3;
}

/**
 * Deja al wrapper con su `evaluations2.Evaluation`, creándola si no la tiene, y devuelve su id.
 *
 * Que falte no es un caso raro: 9.073 piezas quedaron así, todas del tipo Actividad, porque
 * hasta E16 un guard `type != 4` les impedía tener una. Sin ella no hay dónde colgar un
 * instrumento ni una nota, así que se repara al primer guardado que la necesite.
 */
export async function ensureEvaluation(vm, { level_key, anchor_id, wrapper }) {
  if (wrapper.evaluation != null) return wrapper.evaluation;
  const level = levelFor(level_key);

  const new_evaluation = await vm.$store.dispatch(names.POST_NEW_EVALUATION, {
    title: wrapper.name,
    description: wrapper.description,
    weighing: wrapper.weighing,
    visible_by_evaluatees: false,
    parent_evaluation: null,
    files: [],
  });

  // El scope es lo que ubica la evaluación en la asignatura o en el perfil.
  await vm.$restful.Post("/evaluations2/evaluation-scope-default/", {
    scope: level.scope,
    scope_id: anchor_id,
    evaluation: new_evaluation.id,
  });

  // El OneToOne del tipo puede venir ya creado con el Evaluation: ahí se actualiza.
  const type_payload = {
    type: evaluationTypeFor(wrapper),
    evaluation: new_evaluation.id,
  };
  if (new_evaluation.evaluation_type_default == null) {
    await vm.$restful.Post(
      "/evaluations2/evaluation-type-default/",
      type_payload
    );
  } else {
    await vm.$restful.Put(
      `/evaluations2/evaluation-type-default/${new_evaluation.evaluation_type_default}/`,
      { ...type_payload, id: new_evaluation.evaluation_type_default }
    );
  }

  // Recién acá se cierra el vínculo wrapper → Evaluation. Va por la acción del store y no por
  // `$restful` para que la copia que tiene el diseño quede con el vínculo puesto: si no, el
  // próximo guardado la vería sin `Evaluation` y crearía una segunda.
  await vm.$store.dispatch(level.patch_action, {
    [level.patch_id_key]: wrapper.id,
    item: { evaluation: new_evaluation.id },
  });
  wrapper.evaluation = new_evaluation.id;

  // El Evaluation quedó en el store SIN scope ni tipo (se crean más arriba) y el fetch resuelve
  // de caché: sin refrescarlo, la vista del instrumento recibe el objeto a medio construir y
  // queda en sólo lectura hasta un F5.
  await vm.$store
    .dispatch(names.FETCH_NEW_EVALUATION, {
      evaluation_id: new_evaluation.id,
      forceFetch: true,
    })
    .catch(() => null);

  return new_evaluation.id;
}

/**
 * Guarda los instrumentos que declaró el formulario: crea los nuevos, actualiza los que
 * cambiaron y borra los que se marcaron. Es el `bulkCreateTest` del Programa, en un solo lugar.
 *
 * El id que trae una fila nueva es temporal, generado en el navegador para poder editarla antes
 * de guardarla; por eso `isNaN` distingue crear de actualizar.
 */
export async function saveTests(vm, tests, evaluation_id, evaluation_title) {
  const institution = vm.$store.getters.getInstitution;
  const is_duoc = institution && institution.internal_use_id == "duoc.cl";

  for (const test of tests || []) {
    if (test.delete_test) {
      if (!isNaN(test.id)) await vm.$store.dispatch(names.DELETE_NEW_TEST, test.id);
      continue;
    }

    let saved = null;
    if (isNaN(test.id)) {
      const payload = { ...test, evaluation: evaluation_id };
      // Regla de Duoc: el instrumento se llama igual que la evaluación.
      if (is_duoc && evaluation_title) payload.title = evaluation_title;
      // Si viaja, el backend toma el id temporal como el id real.
      delete payload.id;
      saved = await vm.$store.dispatch(names.POST_NEW_TEST, payload);
    } else {
      saved = await vm.$store.dispatch(names.PATCH_NEW_TEST, {
        new_test_id: test.id,
        item: {
          title: test.title,
          evaluation_situation: test.evaluation_situation,
          weighing: test.weighing,
          examiner: test.examiner,
          work_type: test.work_type,
          authentic_evaluation: test.authentic_evaluation,
        },
      });
    }

    if (!saved || !test.instrument_type) continue;
    const type_payload = {
      test: saved.id,
      evaluative_agent: test.instrument_type.evaluative_agent,
    };
    if (saved.test_type_default == null) {
      await vm.$restful.Post("/evaluations2/test-type-default/", type_payload);
    } else {
      await vm.$restful.Put(
        `/evaluations2/test-type-default/${saved.test_type_default}/`,
        type_payload
      );
    }
  }
}

/**
 * Cambia campos sueltos de una pieza: su lugar en la secuencia (`order`, `module`) o su
 * ponderación.
 *
 * Es el mismo PATCH que hace el arrastre. Va por la acción del store para que la copia que
 * tiene el diseño quede con el valor nuevo y no haya que refetchear para verlo.
 */
export async function patchEvaluation(vm, level_key, id, fields) {
  const level = levelFor(level_key);
  return vm.$store.dispatch(level.patch_action, {
    [level.patch_id_key]: id,
    item: fields,
  });
}

/**
 * E45 · Las tres partes que una regeneración puede reemplazar.
 *
 * No son los campos uno por uno, y la agrupación es la parte importante: los cuatro textos
 * —descripción, instrucciones al estudiante, orientación e indicaciones al docente— son
 * cuatro miradas del MISMO trabajo. El texto del estudiante desarrolla pasos sobre un
 * material, la descripción resume esa consigna, las indicaciones al docente anticipan los
 * errores de esa consigna y la orientación explica el lugar de esa pieza. Rehacer uno solo
 * deja la pieza describiendo dos actividades distintas, y eso no se ve: queda incoherente
 * con apariencia de correcta. Van juntos o no van.
 *
 * El nombre sí es separable porque es una etiqueta, y lo que declara de los catálogos
 * también porque son selecciones de listas cerradas, no prosa — y es justo lo que alguien
 * puede haber curado a mano.
 */
export const REDO_PART_NAME = "name";
export const REDO_PART_TEXTS = "texts";
export const REDO_PART_CATALOGS = "catalogs";
export const REDO_PARTS_ALL = [
  REDO_PART_NAME,
  REDO_PART_TEXTS,
  REDO_PART_CATALOGS,
];

/**
 * Regenera con IA una pieza que YA está creada.
 *
 * Reemplaza lo que la pieza DICE y no toca lo que la ubica ni lo que se le colgó después:
 * conserva Unidad, momentos, tipo, horas, ponderación, instrumentos e Indicadores. Esa
 * distinción es lo que separa "quiero otra actividad acá" de "quiero rehacer el diseño";
 * sin ella, regenerar sería destructivo. Los Indicadores están en la lista de lo que no se
 * toca porque de ellos salen los RA (E12): cambiarlos movería la pieza de RA y con eso la
 * cobertura y la ponderación del diseño.
 *
 * Vive acá y no en el modal porque la llaman los dos —el modal de creación rápida y el
 * botón de cada tarjeta—, y es política, no código de pegar: dos copias se separan con el
 * tiempo y una de las dos se vuelve destructiva sin que nadie lo note.
 *
 * No confirma ni avisa: eso es del llamador, que sabe con qué gesto se llegó hasta acá.
 * Devuelve la pieza que escribió la IA, o `null` si no propuso ninguna válida.
 *
 * @param vm                          el componente que llama (para $store, $t y $restful)
 * @param level_key                   "matter" | "egress_profile_matter"
 * @param egress_profile_matter_id    la Asignatura del Perfil de Egreso, si se entró por un plan
 * @param matter_base_id              la Asignatura base, si se entró por ella (E52)
 * @param piece_id                    el wrapper a regenerar
 * @param theme                       tema u orientación opcional para esta pieza
 * @param parts                       qué partes reemplazar (ver REDO_PART_*)
 */
export async function redoPieceWithAi(
  vm,
  {
    level_key,
    egress_profile_matter_id,
    matter_base_id = null,
    piece_id,
    theme = "",
    parts = REDO_PARTS_ALL,
  }
) {
  const { callBuilder } = useAiBuilderStream(
    {
      url: "/common/instructional-builder-ai/",
      generate_error_key: "reusable.aiInstructionalBuilderModal.generateError",
    },
    vm
  );
  // E52 · El builder acepta los dos niveles. Se manda la Asignatura del Perfil cuando
  // hay plan —es el pedido con más contexto, incluye la carrera— y la Asignatura base
  // cuando se entró por ella, que es el único dato que existe en ese nivel.
  const result = await callBuilder({
    ...(egress_profile_matter_id
      ? { egress_profile_matter: egress_profile_matter_id }
      : { matter: matter_base_id }),
    regenerate_piece: piece_id,
    theme: theme || "",
  });
  // El backend devuelve la pieza en el arreglo que corresponde a su tipo, y desde acá no
  // importa cuál sea: se regeneró una sola.
  const fresh =
    ((result || {}).activities || [])[0] || ((result || {}).evaluations || [])[0];
  if (!fresh) return null;

  const fields = {};
  if (parts.includes(REDO_PART_NAME)) {
    fields.name = fresh.name;
  }
  if (parts.includes(REDO_PART_TEXTS)) {
    fields.description = fresh.description;
    fields.description_for_student = fresh.description_for_student;
    fields.professor_instructions = fresh.professor_instructions;
    fields.orientation = fresh.orientation;
  }
  if (parts.includes(REDO_PART_CATALOGS)) {
    fields.work_type = fresh.work_type;
    fields.contents = fresh.contents;
    fields.methodological_strategies = fresh.methodological_strategies;
    fields.procedure_evidences = fresh.procedure_evidences;
  }
  if (Object.keys(fields).length === 0) return fresh;
  await patchEvaluation(vm, level_key, piece_id, fields);
  return fresh;
}

/**
 * Crea una evaluación completa desde el diseño.
 *
 * @param vm            el componente que llama (para $store y $restful)
 * @param level_key     "matter" | "egress_profile_matter"
 * @param anchor_id     id de la Asignatura base o de la Asignatura del Perfil de Egreso
 * @param evaluation    el objeto que emitió el formulario
 * @param time_rows     las filas de horas por Ambiente que emitió el formulario
 * @param tests         los instrumentos que el usuario declaró en el mismo formulario
 * @param placement     { module, order } — dónde queda en la secuencia del diseño
 */
export async function createDesignEvaluation(
  vm,
  { level_key, anchor_id, evaluation, time_rows, tests, placement }
) {
  const level = levelFor(level_key);

  // 1. El wrapper. Nace ya ubicado en la secuencia: si se creara suelto y se moviera después,
  //    el usuario vería la pieza saltar del bloque de "sin ubicar" al Modulo.
  const wrapper = await vm.$restful.Post(level.wrapper_url, {
    ...evaluation,
    [level_key]: anchor_id,
    module: placement.module,
    order: placement.order,
  });

  // 2. Su Evaluation, que es lo que sostiene los instrumentos y las notas. El tipo 4 también
  //    la lleva: sin ella la Actividad no puede tener Test ni calificarse nunca (E16).
  const evaluation_id = await ensureEvaluation(vm, {
    level_key,
    anchor_id,
    wrapper,
  });

  // 3. Los instrumentos, que el formulario deja declarar en la misma pantalla.
  await saveTests(vm, tests, evaluation_id, wrapper.name);

  // 4. Las horas, repartidas por Ambiente de Aprendizaje. Son las que hacen avanzar la semana
  //    en la que cae la pieza (§2.8), así que sin esto el diseño nace sin calendario.
  //
  //    Se filtra el par (Tipo de Hora, Ambiente) repetido antes de mandar nada. El modelo lo
  //    tiene como `unique_together`, y el validador de DRF trata el Ambiente vacío como un
  //    valor más: dos filas sin ambiente y del mismo Tipo de Hora chocan aunque la base de
  //    datos las aceptaría. El formulario puede emitirlas si se declararon horas antes y
  //    después de elegir el ambiente. Gana la que trae ambiente declarado.
  const seen = new Set();
  const rows = [...(time_rows || [])].sort(
    (a, b) => (b.study_environment != null) - (a.study_environment != null)
  );
  for (const row of rows) {
    const key = `${row.time_allocation}-${row.study_environment || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    await vm.$restful.Post(level.time_url, {
      [level.wrapper_fk]: wrapper.id,
      study_environment: row.study_environment,
      time_allocation: row.time_allocation,
      hours: row.hours,
    });
  }

  return wrapper;
}
